import { Controller } from "@hotwired/stimulus";
import { argbFromHex, themeFromSourceColor, hexFromArgb, Blend } from "@material/material-color-utilities";

// Generates M3 color schemes from branding colors and sets them as --theme-*
// CSS variables on the body element so they're available globally.
export default class extends Controller {
  static targets = ["lightButton", "darkButton"];
  static values = {
    accentColor: String,
    secondaryColor: String,
    active: Boolean,
    defaultTheme: { type: String, default: "light" },
  };

  connect() {
    this.materialTheme = this.buildMaterialTheme();
    this.setTheme(this.defaultThemeValue);
  }

  light() {
    this.setTheme("light");
    this.updateButtonStates("light");
  }

  dark() {
    this.setTheme("dark");
    this.updateButtonStates("dark");
  }

  updateButtonStates(theme) {
    if (this.hasLightButtonTarget && this.hasDarkButtonTarget) {
      const isDark = theme === "dark";
      this.lightButtonTarget.disabled = !isDark;
      this.darkButtonTarget.disabled = isDark;
    }
  }

  setTheme(theme) {
    if (this.activeValue && this.materialTheme) {
      this.applyCustomColors(theme);
    } else {
      this.applyDefaultColors(theme);
    }
  }

  buildMaterialTheme() {
    const accent = this.accentColorValue;
    const secondary = this.secondaryColorValue;

    if (!accent || !secondary) return null;

    return themeFromSourceColor(argbFromHex(accent), [
      { blend: false, name: "tertiary", value: argbFromHex(secondary) },
    ]);
  }

  applyDefaultColors(theme) {
    const prefix = theme === "dark" ? "dark" : "light";
    const body = document.body;

    const colorProperties = {
      "--theme-primary": `var(--${prefix}-primary)`,
      "--theme-on-primary": `var(--${prefix}-on-primary)`,
      "--theme-secondary": `var(--${prefix}-secondary)`,
      "--theme-surface": `var(--${prefix}-surface-at-4)`,
      "--theme-on-surface": `var(--${prefix}-on-surface)`,
      "--theme-outline": `var(--${prefix}-outline)`,
      "--theme-surface-at-4": `var(--${prefix}-surface-at-5)`,
    };

    for (const [prop, value] of Object.entries(colorProperties)) {
      body.style.setProperty(prop, value);
    }
  }

  applyCustomColors(theme) {
    const scheme = this.materialTheme.schemes[theme];
    const body = document.body;

    const colorProperties = {
      "--theme-primary": hexFromArgb(scheme.primary),
      "--theme-on-primary": hexFromArgb(scheme.onPrimary),
      "--theme-secondary": hexFromArgb(scheme.secondary),
      "--theme-surface": hexFromArgb(scheme.surface),
      "--theme-on-surface": hexFromArgb(scheme.onSurface),
      "--theme-outline": hexFromArgb(scheme.outline),
    };

    for (const [prop, value] of Object.entries(colorProperties)) {
      body.style.setProperty(prop, value);
    }

    const surfaceAt4 = Blend.cam16Ucs(scheme.surface, scheme.primary, 0.12);
    body.style.setProperty("--theme-surface-at-4", hexFromArgb(surfaceAt4));
  }
}
